import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";


export interface ResourceWorkshop {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  cover_image_url: string | null;
  visibility: "public" | "authenticated";
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ResourceWorkshopItem {
  id: string;
  workshop_id: string;
  type: "link" | "file" | "text" | "embed" | "content";
  title: string;
  description: string | null;
  url: string | null;
  content: string | null;
  is_hidden: boolean;
  file_size: number | null;
  file_mime: string | null;
  sort_order: number;
  created_at: string;
  content_id?: string | null;
  /** Joined fields from content_bank (only present via get_workshop_items RPC) */
  content_name?: string | null;
  content_type?: string | null;
  content_duration?: string | null;
  content_url?: string | null;
  content_vimeo_player_url?: string | null;
  content_html?: string | null;
  content_thumbnail_url?: string | null;
  content_description?: string | null;
}

export interface ContentBankOption {
  id: string;
  name: string;
  content_type: string | null;
  duration: string | null;
  description: string | null;
}

/** Published content_bank items, for the workshop resource picker */
export function useContentBankOptions() {
  return useQuery({
    queryKey: ["content-bank-options"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("content_bank")
        .select("id, name, content_type, duration, description")
        .eq("planning_status", "published")
        .order("name", { ascending: true });
      if (error) throw error;
      return (data || []) as ContentBankOption[];
    },
  });
}


export function useResourceWorkshops() {
  return useQuery({
    queryKey: ["resource-workshops"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("resource_workshops" as any)
        .select("*, items:resource_workshop_items(count)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });
}

export function useResourceWorkshop(id: string | undefined) {
  return useQuery({
    queryKey: ["resource-workshop", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("resource_workshops" as any)
        .select("*")
        .eq("id", id!)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as ResourceWorkshop | null;
    },
  });
}

export function useResourceWorkshopBySlug(slug: string | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["resource-workshop-slug", slug, user?.id ?? "anon"],
    enabled: !!slug,

    queryFn: async () => {
      const { data: workshop, error } = await supabase
        .from("resource_workshops" as any)
        .select("*")
        .eq("slug", slug!)
        .maybeSingle();
      if (error) throw error;
      if (!workshop) return null;
      const { data: items, error: e2 } = await (supabase as any).rpc("get_workshop_items", {
        p_workshop_id: (workshop as any).id,
      });
      if (e2) throw e2;
      return { workshop: workshop as unknown as ResourceWorkshop, items: ((items || []) as unknown) as ResourceWorkshopItem[] };

    },
  });
}

export function useResourceWorkshopItems(workshopId: string | undefined) {
  return useQuery({
    queryKey: ["resource-workshop-items", workshopId],
    enabled: !!workshopId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("resource_workshop_items" as any)
        .select("*")
        .eq("workshop_id", workshopId!)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data || []) as unknown as ResourceWorkshopItem[];
    },
  });
}

export function useWorkshopMutations() {
  const qc = useQueryClient();

  const create = useMutation({
    mutationFn: async (payload: Partial<ResourceWorkshop>) => {
      const { data: u } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from("resource_workshops" as any)
        .insert({ ...payload, created_by: u.user?.id } as any)
        .select()
        .single();
      if (error) throw error;
      return data as any;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resource-workshops"] }),
  });

  const update = useMutation({
    mutationFn: async ({ id, ...payload }: Partial<ResourceWorkshop> & { id: string }) => {
      const { data, error } = await supabase
        .from("resource_workshops" as any)
        .update(payload as any)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as any;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["resource-workshops"] });
      qc.invalidateQueries({ queryKey: ["resource-workshop", vars.id] });
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("resource_workshops" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resource-workshops"] }),
  });

  return { create, update, remove };
}

export function useItemMutations(workshopId: string | undefined) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["resource-workshop-items", workshopId] });

  const create = useMutation({
    mutationFn: async (payload: Partial<ResourceWorkshopItem>) => {
      const { data, error } = await supabase
        .from("resource_workshop_items" as any)
        .insert({ ...payload, workshop_id: workshopId } as any)
        .select()
        .single();
      if (error) throw error;
      return data as any;
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, ...payload }: Partial<ResourceWorkshopItem> & { id: string }) => {
      const { data, error } = await supabase
        .from("resource_workshop_items" as any)
        .update(payload as any)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as any;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("resource_workshop_items" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return { create, update, remove };
}

export async function uploadWorkshopFile(workshopId: string, file: File) {
  const ext = file.name.split(".").pop() || "bin";
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `resource-workshops/${workshopId}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safeName}`;
  const { error } = await supabase.storage.from("content").upload(path, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw error;
  const { data } = supabase.storage.from("content").getPublicUrl(path);
  return { url: data.publicUrl, size: file.size, mime: file.type, path };
}
